import React from 'react';
import {
    Button as AntdButton,
    type ButtonProps as AntdButtonProps,
} from 'antd';

export type SemanticButtonVariant =
    | 'primary'
    | 'secondary'
    | 'danger'
    | 'ghost';
export type ButtonVariant = SemanticButtonVariant | AntdButtonProps['variant'];

export type ButtonProps = Omit<AntdButtonProps, 'variant'> & {
    variant?: ButtonVariant;
};

type ButtonComponent = React.ForwardRefExoticComponent<
    ButtonProps & React.RefAttributes<HTMLButtonElement | HTMLAnchorElement>
> & {
    Group: typeof AntdButton.Group;
    __ANT_BUTTON?: boolean;
};

const semanticVariantProps: Record<
    SemanticButtonVariant,
    Pick<AntdButtonProps, 'color' | 'danger' | 'ghost' | 'type' | 'variant'>
> = {
    primary: { type: 'primary' },
    secondary: { color: 'primary', variant: 'outlined' },
    danger: { danger: true, type: 'primary' },
    ghost: { ghost: true, type: 'default' },
};

const isSemanticVariant = (
    variant: ButtonVariant,
): variant is SemanticButtonVariant => variant in semanticVariantProps;

const Button = React.forwardRef<
    HTMLButtonElement | HTMLAnchorElement,
    ButtonProps
>(({ variant, ...props }, ref) => {
    if (!variant) {
        return <AntdButton ref={ref} {...props} />;
    }

    if (isSemanticVariant(variant)) {
        return (
            <AntdButton
                ref={ref}
                {...props}
                {...semanticVariantProps[variant]}
            />
        );
    }

    return <AntdButton ref={ref} {...props} variant={variant} />;
}) as ButtonComponent;

Button.displayName = 'Button';
Button.Group = (AntdButton as typeof AntdButton | undefined)
    ?.Group as typeof AntdButton.Group;
Button.__ANT_BUTTON = (
    AntdButton as (typeof AntdButton & { __ANT_BUTTON?: boolean }) | undefined
)?.__ANT_BUTTON;

export { AntdButton };
export default Button;
